// =============================================================================
// CHLANN APP
// =============================================================================

import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from './stores/authStore.js';
import { useChatStore } from './stores/chatStore.js';
import { useCallStore } from './stores/callStore.js';
import { useCalendarStore } from './stores/calendarStore.js';
import { usePhotosStore } from './stores/photosStore.js';
import { useDisplayPrefsStore, useResolvedTheme } from './stores/displayPrefsStore.js';

// Lazy-loaded page components (code splitting)
const LandingPage = React.lazy(() => import('./pages/LandingPage.js').then(m => ({ default: m.LandingPage })));
const LoginPage = React.lazy(() => import('./pages/LoginPage.js').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage.js').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage.js').then(m => ({ default: m.ForgotPasswordPage })));
// Public, prerendered pages. Each one is also written to its own HTML document
// at build time (scripts/prerender.mjs), so a crawler that runs no JavaScript
// still gets the text; these routes are what a client-side navigation renders.
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage.js').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('./pages/TermsPage.js').then(m => ({ default: m.TermsPage })));
const InvitePage = React.lazy(() => import('./pages/InvitePage.js').then(m => ({ default: m.InvitePage })));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage.js').then(m => ({ default: m.SettingsPage })));
const FamilyPage = React.lazy(() => import('./pages/FamilyPage.js').then(m => ({ default: m.FamilyPage })));
const CallsPage = React.lazy(() => import('./pages/CallsPage.js').then(m => ({ default: m.CallsPage })));
const PhotosPage = React.lazy(() => import('./pages/PhotosPage.js').then(m => ({ default: m.PhotosPage })));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage.js').then(m => ({ default: m.CalendarPage })));
const DictionaryPage = React.lazy(() => import('./pages/DictionaryPage.js').then(m => ({ default: m.DictionaryPage })));
// DocsPage retired 2026-05-12 — content described pre-v1 self-hosted
// architecture that contradicts the hosted-only positioning in
// docs/business-model-plan.md §7-8. /docs is now 308-redirected to /
// at the Vercel edge (see vercel.json) so the Google ranking equity
// transfers cleanly.
const AdminPage = React.lazy(() => import('./pages/AdminPage.js').then(m => ({ default: m.AdminPage })));

// Lazy-loaded heavy component
const ChatView = React.lazy(() => import('./components/chat/index.js').then(m => ({ default: m.ChatView })));
import { AppLayout } from './components/layout/index.js';
import { IncomingCallModal, ActiveCallView } from './components/calls/index.js';
import { DailyMemoryPrompt } from './components/memory/index.js';
import { InstallPrompt } from './components/pwa/InstallPrompt.js';
import { WelcomeWizard } from './components/wizard/index.js';
import { socketService } from './api/socket.js';
import { api } from './api/client.js';
import { Spinner } from './components/ui/Spinner.js';

// =============================================================================
// AUTH GUARD
// =============================================================================

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isInitialized, isLoading } = useAuthStore();
  const location = useLocation();

  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="xl" className="mx-auto mb-4" />
          <p className="text-ink-3">Ag lódáil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  const { user, isInitialized } = useAuthStore();
  const location = useLocation();

  // Only show loading during initial app load, not during login/register actions
  // Individual pages handle their own loading states via isLoading
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="xl" className="mx-auto mb-4" />
          <p className="text-ink-3">Ag lódáil...</p>
        </div>
      </div>
    );
  }

  if (user) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/chats';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// INTERNAL PAGES
// =============================================================================

function ChatsPage() {
  return (
    <AppLayout hideDesktopHeader>
      <ChatView />
    </AppLayout>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="mb-4 font-display text-6xl font-bold text-primary">404</h1>
        <p className="text-ink-2">Níor aimsíodh an leathanach</p>
        <p className="text-sm text-ink-3">Page not found</p>
        <Link to="/" className="btn-primary mt-8 inline-block">
          Ar ais abhaile
        </Link>
      </div>
    </div>
  );
}

// =============================================================================
// PAGE TRANSITION
// =============================================================================

function PageTransition({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================
// MAIN APP
// =============================================================================

export function App() {
  const { initialize, user } = useAuthStore();
  const { setupSocketListeners } = useChatStore();
  const { setupCallSocketListeners, incomingCall, activeCall } = useCallStore();
  const { handleEventCreated, handleEventUpdated, handleEventDeleted, handleRsvpUpdated } = useCalendarStore();
  const markPhotoIndexed = usePhotosStore((s) => s.markPhotoIndexed);

  // Stable refs for socket listener callbacks to prevent reconnection on store updates
  const socketListenersRef = useRef({ setupSocketListeners, setupCallSocketListeners, handleEventCreated, handleEventUpdated, handleEventDeleted, handleRsvpUpdated, markPhotoIndexed });
  useEffect(() => {
    socketListenersRef.current = { setupSocketListeners, setupCallSocketListeners, handleEventCreated, handleEventUpdated, handleEventDeleted, handleRsvpUpdated, markPhotoIndexed };
  });

  // Initialize auth state on mount
  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Connect socket when authenticated - only depends on `user`
  useEffect(() => {
    if (user) {
      const token = api.getAccessToken();
      if (token) {
        const listeners = socketListenersRef.current;

        // Connect socket with auth token
        socketService.connect(token);

        // Setup store listeners for socket events
        const chatCleanup = listeners.setupSocketListeners();
        const callCleanup = listeners.setupCallSocketListeners();

        // Calendar socket listeners
        const calendarCleanups = [
          socketService.on('event:created', listeners.handleEventCreated),
          socketService.on('event:updated', listeners.handleEventUpdated),
          socketService.on('event:deleted', listeners.handleEventDeleted),
          socketService.on('event:rsvp-updated', listeners.handleRsvpUpdated),
        ];

        // ML / photos socket listeners
        const photosCleanup = socketService.on('photo:indexed', ({ photoId }) => {
          listeners.markPhotoIndexed(photoId);
        });

        return () => {
          chatCleanup();
          callCleanup();
          calendarCleanups.forEach((cleanup) => { cleanup(); });
          photosCleanup();
          socketService.disconnect();
        };
      }
    } else {
      // Disconnect when user logs out
      socketService.disconnect();
    }
   
  }, [user]);


  // P2.12: listen for `notification-click` messages from the service
  // worker and navigate via React Router. Without this listener every
  // tap on a push notification ended up on a freshly-focused tab that
  // never moved to the conversation - the SW posted a message no one
  // was listening for.
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    type SWClickPayload = { type?: string; path?: string };
    function onMessage(event: MessageEvent<SWClickPayload>) {
      const data = event.data;
      if (!data || data.type !== 'notification-click' || !data.path) return;
      // Only navigate within the SPA; refuse absolute URLs.
      if (!data.path.startsWith('/')) return;
      void navigate(data.path);
    }

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => { navigator.serviceWorker.removeEventListener('message', onMessage); };
  }, [navigate]);

  // Comfort Mode comes from user preferences. Both the "simplified" (labelled
  // "Comfort Mode" in Settings) and "accessibility" tiers enlarge targets and
  // text - previously only "accessibility" fired, so the tier literally called
  // Comfort Mode did nothing. MemorySessionView mirrors this predicate.
  const comfort = user?.uiMode === 'simplified' || user?.uiMode === 'accessibility';

  // Display preferences mapped onto the same root that carries data-comfort.
  // theme drives the dark-mode tokens; highContrast/reduceMotion/fontScale are
  // the legibility dial (ui_contrast/ui_motion/ui_scale); dyslexiaFont is
  // device-local.
  const resolvedTheme = useResolvedTheme();
  const highContrast = useDisplayPrefsStore((s) => s.highContrast);
  const dyslexiaFont = useDisplayPrefsStore((s) => s.dyslexiaFont);
  const reduceMotion = useDisplayPrefsStore((s) => s.reduceMotion);
  const fontScale = useDisplayPrefsStore((s) => s.fontScale);
  const useSystemTextSize = useDisplayPrefsStore((s) => s.useSystemTextSize);
  const hydrateFromServer = useDisplayPrefsStore((s) => s.hydrateFromServer);

  // The account-scoped legibility fields are not persisted (see the store), so
  // seed them from the authenticated user whenever the user object changes.
  // This is what makes the setting follow the person across devices.
  useEffect(() => {
    if (user) {
      hydrateFromServer({
        uiScale: user.uiScale,
        uiContrast: user.uiContrast,
        uiMotion: user.uiMotion,
      });
    }
  }, [user, hydrateFromServer]);

  // "Use system text size" defers to the browser/OS base by forcing the
  // multiplier to 1, so the app renders at the platform text size (iOS Dynamic
  // Type) rather than a manual scale.
  const effectiveFontScale = useSystemTextSize ? 1 : fontScale;

  // Font scale must live on <html> so the backing CSS can scale rem-based
  // text (every Tailwind text-* utility) and tap targets. The remaining
  // display attributes stay on the app root div alongside data-comfort.
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveFontScale !== 1) {
      root.style.setProperty('--user-font-scale', String(effectiveFontScale));
    } else {
      root.style.removeProperty('--user-font-scale');
    }
    return () => {
      root.style.removeProperty('--user-font-scale');
    };
  }, [effectiveFontScale]);

  // Mirror the resolved theme onto <html> so body's --surface-1 background is
  // dark from the pre-hydration script (no white flash) and stays correct after
  // a toggle. The app root div keeps its own data-theme too - the compound
  // [data-high-contrast][data-theme] selectors need both attributes on it.
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }, [resolvedTheme]);

  const location = useLocation();

  // Derive a transition key — group chats/:id with chats so switching conversations doesn't re-animate
  const transitionKey = location.pathname.startsWith('/chats') ? '/chats' : location.pathname;

  return (
    <div
      className="min-h-screen bg-surface-1 text-ink-2"
      data-comfort={comfort ? 'true' : undefined}
      data-theme={resolvedTheme === 'dark' ? 'dark' : undefined}
      data-high-contrast={highContrast ? 'true' : undefined}
      data-dyslexia={dyslexiaFont ? 'true' : undefined}
      data-reduce-motion={reduceMotion ? 'true' : undefined}
    >
        {/* Call overlays - rendered above all other content */}
        {incomingCall && <IncomingCallModal />}
        {activeCall && <ActiveCallView />}

        {/* PWA Install Prompt */}
        <InstallPrompt />

        {/* Welcome Wizard for new users */}
        {user && <WelcomeWizard />}

        {/* Proactive "on this day" nudge to look back together */}
        {user && <DailyMemoryPrompt />}

        <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><Spinner size="xl" /></div>}>
        <AnimatePresence mode="wait">
        <Routes location={location} key={transitionKey}>
          {/* Public routes */}
          <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
          {/* /invite explains closed testing to people searching for a sign-up
              form. /register stays as it is: invite emails link to
              /register?code=... and those codes are already out. */}
          <Route path="/invite" element={<PageTransition><InvitePage /></PageTransition>} />
          {/* /docs retired — vercel.json 308-redirects it to /. The
              route is left out of the SPA so direct navigation falls
              through to the platform's redirect rather than being
              caught by the catch-all /* below. */}
          <Route
            path="/login"
            element={
              <RedirectIfAuth>
                <PageTransition><LoginPage /></PageTransition>
              </RedirectIfAuth>
            }
          />
          {/* Sign-up stays invite-only: RegisterPage requires a code, and the
              backend enforces it (GET /api/auth/register-info -> requiresInviteCode).
              The route must exist or invite links have nowhere to land. */}
          <Route
            path="/register"
            element={
              <RedirectIfAuth>
                <PageTransition><RegisterPage /></PageTransition>
              </RedirectIfAuth>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RedirectIfAuth>
                <PageTransition><ForgotPasswordPage /></PageTransition>
              </RedirectIfAuth>
            }
          />

          {/* Protected routes */}
          <Route
            path="/chats"
            element={
              <RequireAuth>
                <ChatsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/chats/:id"
            element={
              <RequireAuth>
                <ChatsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/family"
            element={
              <RequireAuth>
                <FamilyPage />
              </RequireAuth>
            }
          />
          <Route
            path="/calls"
            element={
              <RequireAuth>
                <CallsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/photos"
            element={
              <RequireAuth>
                <PhotosPage />
              </RequireAuth>
            }
          />
          <Route
            path="/calendar"
            element={
              <RequireAuth>
                <CalendarPage />
              </RequireAuth>
            }
          />

          <Route
            path="/dictionary"
            element={
              <RequireAuth>
                <DictionaryPage />
              </RequireAuth>
            }
          />

          {/* Admin - the page itself gates on user.isAdmin */}
          <Route path="/admin" element={<PageTransition><AdminPage /></PageTransition>} />

          {/* 404 */}
          <Route path="/404" element={<PageTransition><NotFoundPage /></PageTransition>} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        </AnimatePresence>
        </Suspense>
    </div>
  );
}
